/**
 * CAD MODIFY OPERATIONS (CAD round, 2026-08-15 — docs/MODELER.md "DETAIL CAD"): the
 * AutoCAD modify verbs over detail drafting records, driven by the detail command
 * line. Everything here is ARC-TRUE through the one shared arc.ts derivation —
 * intersections are line∩line / line∩circle / circle∩circle closed forms filtered to
 * the segment/sweep, and every cut or extended arc edge re-derives its bulge from the
 * sub-sweep (never re-fit from facets). One transact = one undo step per verb, the
 * standing command contract; every verb refuses records owned by a FIRM-library
 * detail (firmDetailLocked — "Duplicate to customize" is the editing path).
 *
 * Scope of v1 (the honest ledger, mirrored in docs/MODELER.md):
 * - transforms (move/copy/rotate/mirror/scale) apply to line/region/text/dimension/
 *   spot records; SCALE moves text/dimension ANCHORS but not their paper-space sizes
 *   (annotation sizes live on types — the annotation scale model).
 * - trim/extend/fillet operate on LINE records (regions are closed boundaries —
 *   reshape them by grips); fillet is straight-segment only (arc fillets recorded).
 * - offset copies line/region records whole (the mitered arc-aware offsetPolyline).
 */
import type { DraftingId, DraftingRecord, DraftingWitness, ElementId, NodeId, Path2D, PathNode, Pt2 } from '../types'
import { newDraftingId, newNodeId } from '../ids'
import { getDrafting, listDrafting, maps, recordToYMap, transact, type ModelDoc } from '../doc'
import { draftingForSurface, firmDetailLocked, type DraftingScope, type DraftingSurface } from './drafting'
import { mirrorPath, mirrorPoint, offsetPolyline, reversePath, rotatePath, rotatePoint } from '../pathOps'
import {
  BULGE_EPS, bulgeToArc, flattenPathPts, projectToArc, lineCircleIntersect, circleCircleIntersect,
  type EdgeArc,
} from '../arc'

const EPS = 1e-9

// ---------- transforms ----------

export type DraftXform =
  | { kind: 'translate'; d: Pt2 }
  | { kind: 'rotate'; c: Pt2; deg: number }
  | { kind: 'mirror'; a: Pt2; b: Pt2 }
  | { kind: 'scale'; c: Pt2; k: number }

function xfPoint(xf: DraftXform, p: Pt2): Pt2 {
  switch (xf.kind) {
    case 'translate': return [p[0] + xf.d[0], p[1] + xf.d[1]]
    case 'rotate': return rotatePoint(p, xf.c, xf.deg)
    case 'mirror': return mirrorPoint(p, xf.a, [xf.b[0] - xf.a[0], xf.b[1] - xf.a[1]])
    case 'scale': return [xf.c[0] + (p[0] - xf.c[0]) * xf.k, xf.c[1] + (p[1] - xf.c[1]) * xf.k]
  }
}

function xfPath(xf: DraftXform, path: Path2D): Path2D {
  if (xf.kind === 'rotate') return rotatePath(path, xf.c, xf.deg)
  if (xf.kind === 'mirror') return mirrorPath(path, xf.a, [xf.b[0] - xf.a[0], xf.b[1] - xf.a[1]])
  // translate/scale: point maps; bulge is chord-relative so a uniform scale keeps it
  return { closed: path.closed, nodes: path.nodes.map((n) => ({ ...n, pt: xfPoint(xf, n.pt) })) }
}

const xfWitness = (xf: DraftXform, w: DraftingWitness): DraftingWitness =>
  ({ ...w, pt: xfPoint(xf, w.pt) })

/** the transformed plain record (geometry fields only — identity/scope untouched) */
function transformedFields(xf: DraftXform, rec: DraftingRecord): Partial<DraftingRecord> {
  const out: Partial<DraftingRecord> = {}
  if (rec.path) out.path = xfPath(xf, rec.path)
  if (rec.pt) out.pt = xfPoint(xf, rec.pt)
  if (rec.leader) out.leader = xfPoint(xf, rec.leader)
  if (rec.leaders?.length) out.leaders = rec.leaders.map((p) => xfPoint(xf, p))
  if (rec.bend) out.bend = xfPoint(xf, rec.bend)
  if (rec.a) out.a = xfWitness(xf, rec.a)
  if (rec.b) out.b = xfWitness(xf, rec.b)
  if (rec.witnesses?.length) out.witnesses = rec.witnesses.map((w) => xfWitness(xf, w))
  // a dimension's offset is signed-perpendicular in the string's own frame — a mirror
  // flips handedness, so the offset negates to keep the line on the same visual side
  if (rec.kind === 'dimension' && rec.offsetFt != null && xf.kind === 'mirror') out.offsetFt = -rec.offsetFt
  // COMPONENT LENGTH GRIP: a placed component's frame moves with it
  if (rec.componentPlace?.at) {
    const cp = rec.componentPlace
    const at = xfPoint(xf, cp.at)
    const dirOf = (v: Pt2): { u: Pt2; len: number } => {
      const q = xfPoint(xf, [cp.at[0] + v[0], cp.at[1] + v[1]])
      const d: Pt2 = [q[0] - at[0], q[1] - at[1]]
      const len = Math.hypot(d[0], d[1]) || 1
      return { u: [d[0] / len, d[1] / len], len }
    }
    const X = dirOf(cp.ax), Y = dirOf(cp.ay)
    out.componentPlace = { ...cp, at, ax: X.u, ay: Y.u, k: (cp.k ?? 1) * X.len }
  }
  return out
}

/**
 * Fresh-id deep copy of a record's transformed form (the GL5b duplicate contract).
 * DRAFTING BASELINE: `ctx` (one per copy pass) gives every source GROUP one new group
 * id — a copy of a group is a new group, never a member of the original — and records
 * old→new record + node ids so `remapCopiedRefs` can re-point a copied dimension at
 * the copied linework.
 */
interface CopyCtx { groups: Map<string, string>; recs: Map<string, string>; nodes: Map<string, string> }
const newCopyCtx = (): CopyCtx => ({ groups: new Map(), recs: new Map(), nodes: new Map() })
const newGroupId = () => 'dg_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
function copyRecord(rec: DraftingRecord, fields: Partial<DraftingRecord>, ctx?: CopyCtx): DraftingRecord {
  const copy = JSON.parse(JSON.stringify({ ...rec, ...fields })) as DraftingRecord
  copy.id = newDraftingId()
  ctx?.recs.set(rec.id, copy.id)
  if (copy.path) {
    copy.path = {
      closed: copy.path.closed,
      nodes: copy.path.nodes.map((n) => { const id = newNodeId(); ctx?.nodes.set(`${rec.id}|${n.id}`, id); return { ...n, id } }),
    }
  }
  if (rec.groupId && ctx) {
    let g = ctx.groups.get(rec.groupId)
    if (!g) { g = newGroupId(); ctx.groups.set(rec.groupId, g) }
    copy.groupId = g
  }
  return copy
}
/** re-point copied dimensions' draftRef witnesses at the copied records (same pass) */
function remapCopiedRefs(copies: DraftingRecord[], ctx: CopyCtx): void {
  const fix = (w: DraftingWitness): DraftingWitness => {
    if (!w.draftRef) return w
    const r = ctx.recs.get(w.draftRef.recId)
    const n = ctx.nodes.get(`${w.draftRef.recId}|${w.draftRef.nodeId}`)
    return r && n ? { ...w, draftRef: { recId: r as DraftingId, nodeId: n as NodeId } } : w
  }
  for (const c of copies) {
    if (c.kind !== 'dimension') continue
    // DRAFTING PARITY: a radius / arc-length dimension on a drafted arc copied with it
    // measures the copy's arc
    const er = c.edgeRef
    if (er && er.elementId.startsWith('draft:')) {
      const oldRec = er.elementId.slice(6)
      const r = ctx.recs.get(oldRec)
      const n = ctx.nodes.get(`${oldRec}|${er.nodeId}`)
      if (r && n) c.edgeRef = { elementId: `draft:${r}` as ElementId, nodeId: n as NodeId }
    }
    if (c.witnesses) c.witnesses = c.witnesses.map(fix)
    if (c.a) c.a = fix(c.a)
    if (c.b) c.b = fix(c.b)
  }
}

/** DRAFTING BASELINE: every record id in the groups of `ids` (plus ungrouped ids) */
export function expandDraftGroups(model: ModelDoc, ids: DraftingId[]): DraftingId[] {
  const groups = new Set<string>()
  const out = new Set<DraftingId>()
  for (const id of ids) {
    const r = getDrafting(model, id)
    if (!r) continue
    out.add(id)
    if (r.groupId) groups.add(r.groupId)
  }
  if (groups.size) for (const r of listDrafting(model)) if (r.groupId && groups.has(r.groupId)) out.add(r.id)
  return [...out]
}

/** DRAFTING BASELINE: GROUP — the records (and the groups they're in) become one
 *  new group. Returns the new group id, or null when nothing editable. */
export function groupDrafting(model: ModelDoc, ids: DraftingId[]): string | null {
  const all = expandDraftGroups(model, ids)
  const recs = editableRecords(model, all)
  if (!recs || recs.length < 2) return null
  const g = newGroupId()
  transact(model, () => {
    const { drafting } = maps(model)
    for (const r of recs) drafting.get(r.id)?.set('groupId', g)
  })
  return g
}

/** DRAFTING BASELINE: UNGROUP — every group touched by `ids` dissolves (members stay).
 *  Returns how many records were released. */
export function ungroupDrafting(model: ModelDoc, ids: DraftingId[]): number {
  const all = expandDraftGroups(model, ids)
  const recs = editableRecords(model, all)
  if (!recs) return 0
  let n = 0
  transact(model, () => {
    const { drafting } = maps(model)
    for (const r of recs) {
      const m = drafting.get(r.id)
      if (m && (m.get('groupId') || m.get('componentId'))) { m.delete('groupId'); m.delete('componentId'); n++ }
    }
  })
  return n
}

function editableRecords(model: ModelDoc, ids: DraftingId[]): DraftingRecord[] | null {
  const out: DraftingRecord[] = []
  for (const id of ids) {
    const rec = getDrafting(model, id)
    if (!rec || firmDetailLocked(model, rec.scope)) return null
    out.push(rec)
  }
  return out.length ? out : null
}

/** COPY FIDELITY: records in their surface's draw order (bottom → top) */
function inDrawOrder(model: ModelDoc, recs: DraftingRecord[]): DraftingRecord[] {
  if (recs.length < 2) return recs
  const rank = new Map<string, number>()
  for (const r of recs) {
    if (rank.has(r.id)) continue
    draftingForSurface(model, surfaceOfScope(r.scope)).forEach((x, i) => { if (!rank.has(x.id)) rank.set(x.id, i) })
  }
  return [...recs].sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0))
}

/**
 * MOVE / COPY / ROTATE / MIRROR / SCALE over a selection set — in place, or as
 * fresh-id copies (`copy: true`, the AutoCAD Copy / mirror-keep-source semantics).
 * Returns the affected (or newly created) record ids; null = nothing editable.
 */
export function transformDrafting(
  model: ModelDoc,
  ids: DraftingId[],
  xf: DraftXform,
  opts?: { copy?: boolean },
): DraftingId[] | null {
  let recs = editableRecords(model, ids)
  if (!recs) return null
  // COPY FIDELITY (2026-10-13, Matt: "I did a move copy of the breakline symbol and the
  // lineweights did not copy with it … all detail components and symbols copy exactly as
  // they are"): copies are written in the source's DRAW ORDER (bottom → top) with no
  // stored zOrder, so they land on top of the view as new items with their own pieces
  // stacked exactly as the original's. Selection order used to decide it — a break
  // line's mask could land OVER its own line and hide half the stroke.
  if (opts?.copy) recs = inDrawOrder(model, recs)
  const out: DraftingId[] = []
  const ctx = newCopyCtx()
  const copies: DraftingRecord[] = []
  transact(model, () => {
    const { drafting } = maps(model)
    for (const rec of recs) {
      const fields = transformedFields(xf, rec)
      if (opts?.copy) {
        const c = copyRecord(rec, fields, ctx)
        delete (c as { zOrder?: number }).zOrder
        copies.push(c)
      } else {
        const m = drafting.get(rec.id)
        if (!m) continue
        for (const [k, v] of Object.entries(fields)) m.set(k, v)
        out.push(rec.id)
      }
    }
    if (copies.length) {
      remapCopiedRefs(copies, ctx)
      for (const copy of copies) {
        drafting.set(copy.id, recordToYMap(copy as unknown as Record<string, unknown>))
        out.push(copy.id)
      }
    }
  })
  return out
}

/** ERASE: delete a selection set in ONE transaction (one undo step, unlike a
 *  deleteDrafting loop). Returns how many records went; 0 = nothing editable. */
export function eraseDrafting(model: ModelDoc, ids: DraftingId[]): number {
  const recs = editableRecords(model, ids)
  if (!recs) return 0
  transact(model, () => {
    const { drafting } = maps(model)
    for (const r of recs) drafting.delete(r.id)
  })
  return recs.length
}

/**
 * STRETCH (Matt: "we need the stretch command"): moves only the points of a
 * selection that fall INSIDE the crossing rect by `delta` — every other point on
 * the same records stays put, so a window crossing just one end of a line/
 * dimension/leader moves that end and reshapes the rest (the AutoCAD "warp by a
 * window" behavior), instead of translating whole objects (that's MOVE). Bulges
 * are left untouched — a moved endpoint's arc re-derives against the new chord at
 * the same sweep (the offset/fillet convention elsewhere in this file). `rect` is
 * [x0,y0,x1,y1] in ORIGINAL coordinates, read once before any point moves so the
 * window can never re-capture a point it just displaced. A witness/dimension
 * point still tied to LIVE model geometry (`ref` set) is skipped — irrelevant here
 * in practice (details have no model elements) but keeps the op honest if that
 * ever changes. Returns the touched ids, or null when nothing fell inside the
 * window (locked / not found / no point inside the rect).
 */
export function stretchDraftingNodes(
  model: ModelDoc,
  ids: DraftingId[],
  rect: [number, number, number, number],
  delta: Pt2,
): DraftingId[] | null {
  const recs = editableRecords(model, ids)
  if (!recs) return null
  const [x0, y0, x1, y1] = rect
  const inside = (p: Pt2) => p[0] >= x0 && p[0] <= x1 && p[1] >= y0 && p[1] <= y1
  const move = (p: Pt2): Pt2 => (inside(p) ? [p[0] + delta[0], p[1] + delta[1]] : p)
  const touched: DraftingId[] = []
  transact(model, () => {
    const { drafting } = maps(model)
    for (const rec of recs) {
      const m = drafting.get(rec.id)
      if (!m) continue
      const patch: Partial<DraftingRecord> = {}
      let hit = false
      if (rec.path) {
        const nodes = rec.path.nodes.map((n) => {
          const np = move(n.pt)
          if (np !== n.pt) hit = true
          return np === n.pt ? n : { ...n, pt: np }
        })
        if (hit) patch.path = { closed: rec.path.closed, nodes }
      }
      if (rec.pt) { const np = move(rec.pt); if (np !== rec.pt) { patch.pt = np; hit = true } }
      if (rec.leader) { const np = move(rec.leader); if (np !== rec.leader) { patch.leader = np; hit = true } }
      if (rec.leaders?.length) {
        const ls = rec.leaders.map((p) => move(p))
        if (ls.some((p, i) => p !== rec.leaders![i])) { patch.leaders = ls; hit = true }
      }
      if (rec.bend) { const np = move(rec.bend); if (np !== rec.bend) { patch.bend = np; hit = true } }
      if (rec.a && !rec.a.ref) { const np = move(rec.a.pt); if (np !== rec.a.pt) { patch.a = { ...rec.a, pt: np }; hit = true } }
      if (rec.b && !rec.b.ref) { const np = move(rec.b.pt); if (np !== rec.b.pt) { patch.b = { ...rec.b, pt: np }; hit = true } }
      if (rec.witnesses?.length) {
        const ws = rec.witnesses.map((w) => {
          if (w.ref) return w
          const np = move(w.pt)
          return np === w.pt ? w : { ...w, pt: np }
        })
        if (ws.some((w, i) => w !== rec.witnesses![i])) { patch.witnesses = ws; hit = true }
      }
      if (!hit) continue
      for (const [k, v] of Object.entries(patch)) m.set(k, v)
      touched.push(rec.id)
    }
  })
  return touched.length ? touched : null
}

/**
 * EXPLODE (Matt: "we need to be able to explode polylines if we want"): a
 * multi-segment 'line' record becomes N independent 2-node line records, one per
 * edge — each keeps its OWN bulge, so a curved segment explodes into its own arc,
 * never a flattened chord. Only a 'line' with 2+ edges is eligible — a region is a
 * closed hatch boundary (reshape it by grips instead) and a single-segment line has
 * nothing to explode; those are silently skipped (the AutoCAD multi-select
 * behavior — explode whatever qualifies, leave the rest untouched), same as a
 * mixed selection through eraseDrafting/transformDrafting. One transact = one undo
 * step for the whole selection. Returns null when NOTHING in the selection
 * qualified (locked / not found / no eligible polylines).
 */
export function explodeDraftPath(
  model: ModelDoc,
  ids: DraftingId[],
): { exploded: number; newIds: DraftingId[] } | null {
  const recs = editableRecords(model, ids)
  if (!recs) return null
  const eligible = recs
    .filter((rec) => rec.kind === 'line' && !!rec.path)
    .map((rec) => ({ rec, edges: edgesOf(rec.path!) }))
    .filter(({ edges }) => edges.length >= 2)
  if (!eligible.length) return null
  const newIds: DraftingId[] = []
  let exploded = 0
  transact(model, () => {
    const { drafting } = maps(model)
    for (const { rec, edges } of eligible) {
      for (const e of edges) {
        const newId = newDraftingId()
        const path: Path2D = {
          closed: false,
          nodes: [
            { id: newNodeId(), pt: e.a, ...(e.bulge ? { bulge: e.bulge } : {}) },
            { id: newNodeId(), pt: e.b },
          ],
        }
        drafting.set(newId, recordToYMap({
          id: newId, scope: rec.scope, kind: 'line', path,
          ...(rec.layerId ? { layerId: rec.layerId } : {}),
          ...(rec.component ? { component: rec.component } : {}),
        } as unknown as Record<string, unknown>))
        newIds.push(newId)
      }
      drafting.delete(rec.id)
      exploded++
    }
  })
  return exploded > 0 ? { exploded, newIds } : null
}

/** endpoint-coincidence rounding grid for JOIN — generous enough to absorb
 *  floating-point drift from prior transforms, tight enough to never merge
 *  two points a user would consider visually distinct */
const JOIN_KEY_EPS = 1e-4

/**
 * JOIN — the inverse of EXPLODE: merges selected open 'line' records into a
 * single polyline wherever their free ends coincide, chaining through
 * degree-2 junctions only (a THREE-OR-MORE-way meeting point is a branch —
 * ambiguous which pair continues the chain, so it's left untouched, the same
 * "unambiguous runs only" rule AutoCAD's JOIN applies). A chain that closes
 * back on its own start becomes `closed: true` (the duplicate coincident end
 * node dropped). Ineligible members (closed loops, regions, single-segment-
 * only... actually single points are fine, only non-line/no-path/already-
 * closed are excluded) are silently left alone, same as explodeDraftPath's
 * "explode whatever qualifies" convention. One transact = one undo step for
 * every chain the whole selection resolves into. Returns null when nothing
 * in the selection joined to anything else.
 */
export function joinDraftLines(
  model: ModelDoc,
  ids: DraftingId[],
): { joined: number; newIds: DraftingId[] } | null {
  const recs = editableRecords(model, ids)
  if (!recs) return null
  const eligible = recs.filter(
    (rec) => rec.kind === 'line' && !!rec.path && !rec.path.closed && rec.path.nodes.length >= 2,
  )
  if (eligible.length < 2) return null

  const keyOf = (pt: Pt2) => `${Math.round(pt[0] / JOIN_KEY_EPS)},${Math.round(pt[1] / JOIN_KEY_EPS)}`
  const endPt = eligible.map((rec) => {
    const ns = rec.path!.nodes
    return { a: ns[0].pt, b: ns[ns.length - 1].pt }
  })
  type End = { idx: number; end: 'a' | 'b' }
  const byKey = new Map<string, End[]>()
  endPt.forEach((e, idx) => {
    for (const end of ['a', 'b'] as const) {
      const k = keyOf(e[end])
      const arr = byKey.get(k)
      if (arr) arr.push({ idx, end })
      else byKey.set(k, [{ idx, end }])
    }
  })

  const used = new Set<number>()
  const newIds: DraftingId[] = []
  let joinedCount = 0

  transact(model, () => {
    const { drafting } = maps(model)
    for (let i = 0; i < eligible.length; i++) {
      if (used.has(i)) continue
      used.add(i)
      const thisChain = [i]
      const chainPaths: Path2D[] = [eligible[i].path!]
      let frontPt = endPt[i].a
      let backPt = endPt[i].b
      let closedLoop = false

      // grow the back — a member matching by its OWN 'a' end is already
      // correctly oriented to append; matching by 'b' needs reversing first
      for (;;) {
        const hits = (byKey.get(keyOf(backPt)) || []).filter((h) => !used.has(h.idx))
        if (hits.length !== 1) break
        const { idx, end } = hits[0]
        used.add(idx); thisChain.push(idx)
        let p = eligible[idx].path!
        if (end === 'b') p = reversePath(p)
        chainPaths.push(p)
        backPt = p.nodes[p.nodes.length - 1].pt
        if (keyOf(backPt) === keyOf(frontPt)) { closedLoop = true; break }
      }
      // grow the front (skip if the back-growth already closed the loop)
      if (!closedLoop) {
        for (;;) {
          const hits = (byKey.get(keyOf(frontPt)) || []).filter((h) => !used.has(h.idx))
          if (hits.length !== 1) break
          const { idx, end } = hits[0]
          used.add(idx); thisChain.push(idx)
          let p = eligible[idx].path!
          if (end === 'a') p = reversePath(p)
          chainPaths.unshift(p)
          frontPt = p.nodes[0].pt
          if (keyOf(frontPt) === keyOf(backPt)) { closedLoop = true; break }
        }
      }

      if (thisChain.length < 2) { used.delete(i); continue }

      // splice: the first member's nodes whole, then each next member's
      // nodes minus its first (the point it shares with the running chain) —
      // that shared node's bulge (if the running end had none) carries over,
      // since it still describes the same point continuing the same edge
      const nodes: PathNode[] = chainPaths[0].nodes.map((n) => ({ ...n }))
      for (let k = 1; k < chainPaths.length; k++) {
        const rest = chainPaths[k].nodes
        const prevLast = nodes[nodes.length - 1]
        if (!prevLast.bulge && rest[0].bulge) prevLast.bulge = rest[0].bulge
        nodes.push(...rest.slice(1).map((n) => ({ ...n })))
      }
      let closed = false
      if (closedLoop) {
        closed = true
        const last = nodes.pop()!
        if (last.bulge && !nodes[nodes.length - 1].bulge) nodes[nodes.length - 1].bulge = last.bulge
      }

      const anchor = eligible[i]
      const newId = newDraftingId()
      drafting.set(newId, recordToYMap({
        id: newId, scope: anchor.scope, kind: 'line',
        path: {
          closed,
          nodes: nodes.map((n) => ({ id: newNodeId(), pt: n.pt, ...(n.bulge ? { bulge: n.bulge } : {}) })),
        },
        ...(anchor.layerId ? { layerId: anchor.layerId } : {}),
        ...(anchor.component ? { component: anchor.component } : {}),
      } as unknown as Record<string, unknown>))
      for (const k of thisChain) drafting.delete(eligible[k].id)
      newIds.push(newId)
      joinedCount += thisChain.length
    }
  })

  return joinedCount > 0 ? { joined: joinedCount, newIds } : null
}

/**
 * ARRAY (Matt's mid-round additions): 'linear' lays count−1 copies at k·d from the
 * originals; 'polar' spins them about `c` — full circle spaces count evenly
 * (360/count), a partial fill divides count−1 gaps across it (the AutoCAD rule).
 * One transact = one undo for the whole array.
 */
export function arrayDrafting(
  model: ModelDoc,
  ids: DraftingId[],
  spec: { kind: 'linear'; d: Pt2; count: number } | { kind: 'polar'; c: Pt2; count: number; fillDeg?: number },
): DraftingId[] | null {
  const found = editableRecords(model, ids)
  if (!found || spec.count < 2) return null
  // COPY FIDELITY: every repetition stacks its pieces as the original does, on top
  const recs = inDrawOrder(model, found)
  const out: DraftingId[] = []
  transact(model, () => {
    const { drafting } = maps(model)
    for (let k = 1; k < spec.count; k++) {
      const ctx = newCopyCtx()   // DRAFTING BASELINE: each repetition of a group is its own group
      const copies: DraftingRecord[] = []
      const xf: DraftXform = spec.kind === 'linear'
        ? { kind: 'translate', d: [spec.d[0] * k, spec.d[1] * k] }
        : {
          kind: 'rotate', c: spec.c,
          deg: k * (Math.abs((spec.fillDeg ?? 360) - 360) < 1e-9 || Math.abs((spec.fillDeg ?? 360) + 360) < 1e-9
            ? (spec.fillDeg ?? 360) / spec.count
            : (spec.fillDeg ?? 360) / (spec.count - 1)),
        }
      for (const rec of recs) {
        const c = copyRecord(rec, transformedFields(xf, rec), ctx)
        delete (c as { zOrder?: number }).zOrder
        copies.push(c)
      }
      remapCopiedRefs(copies, ctx)
      for (const copy of copies) {
        drafting.set(copy.id, recordToYMap(copy as unknown as Record<string, unknown>))
        out.push(copy.id)
      }
    }
  })
  return out
}

// ---------- path parameterization + intersections (arc-true) ----------

/** a position on a path: edge index + fraction (arcs: fraction OF THE SWEEP) */
export interface PathParam { edge: number; t: number; pt: Pt2 }

interface EdgeGeom { a: Pt2; b: Pt2; bulge?: number; arc: EdgeArc | null }

function edgesOf(path: Path2D): EdgeGeom[] {
  const n = path.nodes.length
  const count = path.closed ? n : n - 1
  const out: EdgeGeom[] = []
  for (let i = 0; i < count; i++) {
    const a = path.nodes[i].pt, b = path.nodes[(i + 1) % n].pt
    out.push({ a, b, bulge: path.nodes[i].bulge, arc: bulgeToArc(a, b, path.nodes[i].bulge) })
  }
  return out
}

/** the point at fraction t of an edge (arcs: t of the sweep) */
function edgePointAt(e: EdgeGeom, t: number): Pt2 {
  if (e.arc) {
    const ang = e.arc.a0 + e.arc.sweep * t
    return [e.arc.c[0] + e.arc.r * Math.cos(ang), e.arc.c[1] + e.arc.r * Math.sin(ang)]
  }
  return [e.a[0] + (e.b[0] - e.a[0]) * t, e.a[1] + (e.b[1] - e.a[1]) * t]
}

/** param of a point KNOWN to lie on the edge's curve */
function edgeParamOf(e: EdgeGeom, pt: Pt2): number {
  if (e.arc) {
    const ang = Math.atan2(pt[1] - e.arc.c[1], pt[0] - e.arc.c[0])
    const norm = (v: number) => { let x = v % (Math.PI * 2); if (x < 0) x += Math.PI * 2; return x }
    const d = e.arc.sweep >= 0 ? norm(ang - e.arc.a0) : norm(e.arc.a0 - ang)
    return Math.min(1, d / Math.abs(e.arc.sweep))
  }
  const dx = e.b[0] - e.a[0], dy = e.b[1] - e.a[1]
  const L2 = dx * dx + dy * dy
  if (L2 < EPS) return 0
  return Math.max(0, Math.min(1, ((pt[0] - e.a[0]) * dx + (pt[1] - e.a[1]) * dy) / L2))
}

/** is `pt` (a root on the edge's infinite carrier) within the bounded edge? */
function onEdge(e: EdgeGeom, pt: Pt2, pad = 1e-7): number | null {
  if (e.arc) {
    const ang = Math.atan2(pt[1] - e.arc.c[1], pt[0] - e.arc.c[0])
    const norm = (v: number) => { let x = v % (Math.PI * 2); if (x < 0) x += Math.PI * 2; return x }
    const d = e.arc.sweep >= 0 ? norm(ang - e.arc.a0) : norm(e.arc.a0 - ang)
    const sw = Math.abs(e.arc.sweep)
    if (d <= sw + pad) return Math.min(1, d / sw)
    if (d >= Math.PI * 2 - pad) return 0
    return null
  }
  const dx = e.b[0] - e.a[0], dy = e.b[1] - e.a[1]
  const L = Math.hypot(dx, dy)
  if (L < EPS) return null
  const t = ((pt[0] - e.a[0]) * dx + (pt[1] - e.a[1]) * dy) / (L * L)
  if (t < -pad / L || t > 1 + pad / L) return null
  // reject roots off the carrier line (a circle root can satisfy the param check)
  const q = edgePointAt(e, Math.max(0, Math.min(1, t)))
  if (Math.hypot(q[0] - pt[0], q[1] - pt[1]) > 1e-6) return null
  return Math.max(0, Math.min(1, t))
}

/** intersection points of two bounded edges (line/line, line/arc, arc/arc) */
function edgeEdgeHits(ea: EdgeGeom, eb: EdgeGeom): Pt2[] {
  let roots: Pt2[] = []
  if (!ea.arc && !eb.arc) {
    const d1: Pt2 = [ea.b[0] - ea.a[0], ea.b[1] - ea.a[1]]
    const d2: Pt2 = [eb.b[0] - eb.a[0], eb.b[1] - eb.a[1]]
    const cross = d1[0] * d2[1] - d1[1] * d2[0]
    if (Math.abs(cross) < EPS) return []
    const dx = eb.a[0] - ea.a[0], dy = eb.a[1] - ea.a[1]
    const t = (dx * d2[1] - dy * d2[0]) / cross
    roots = [[ea.a[0] + d1[0] * t, ea.a[1] + d1[1] * t]]
  } else if (ea.arc && eb.arc) {
    roots = circleCircleIntersect(ea.arc.c, ea.arc.r, eb.arc.c, eb.arc.r)
  } else {
    const line = ea.arc ? eb : ea
    const arc = (ea.arc ?? eb.arc)!
    roots = lineCircleIntersect(line.a, [line.b[0] - line.a[0], line.b[1] - line.a[1]], arc.c, arc.r)
  }
  return roots.filter((p) => onEdge(ea, p) !== null && onEdge(eb, p) !== null)
}

/** every intersection of `path` with `other`, as params ON `path` */
export function pathIntersectionParams(path: Path2D, other: Path2D): PathParam[] {
  const eas = edgesOf(path)
  const ebs = edgesOf(other)
  const out: PathParam[] = []
  eas.forEach((ea, i) => {
    for (const eb of ebs) {
      for (const pt of edgeEdgeHits(ea, eb)) {
        const t = onEdge(ea, pt)
        if (t !== null) out.push({ edge: i, t, pt })
      }
    }
  })
  return out
}

/** nearest on-path param to a free point (projection over every edge) */
export function projectToPathParam(path: Path2D, p: Pt2): PathParam {
  const edges = edgesOf(path)
  let best: PathParam = { edge: 0, t: 0, pt: edges[0]?.a ?? [0, 0] }
  let bd = Infinity
  edges.forEach((e, i) => {
    let cand: { pt: Pt2; t: number } | null = null
    if (e.arc) {
      const proj = projectToArc(e.arc, p)
      if (proj) cand = { pt: proj.pt, t: edgeParamOf(e, proj.pt) }
      else {
        const dA = Math.hypot(p[0] - e.a[0], p[1] - e.a[1])
        const dB = Math.hypot(p[0] - e.b[0], p[1] - e.b[1])
        cand = dA <= dB ? { pt: e.a, t: 0 } : { pt: e.b, t: 1 }
      }
    } else {
      const t = edgeParamOf(e, p)
      cand = { pt: edgePointAt(e, t), t }
    }
    const d = Math.hypot(p[0] - cand.pt[0], p[1] - cand.pt[1])
    if (d < bd) { bd = d; best = { edge: i, t: cand.t, pt: cand.pt } }
  })
  return best
}

/** sub-edge bulge for the [t0, t1] portion of an edge (arcs: the sub-sweep's tan/4) */
function portionBulge(e: EdgeGeom, t0: number, t1: number): number | undefined {
  if (!e.arc) return undefined
  const b = Math.tan((e.arc.sweep * (t1 - t0)) / 4)
  return Math.abs(b) < BULGE_EPS ? undefined : b
}

const ordinal = (c: PathParam) => c.edge + Math.max(0, Math.min(1, c.t)) * 0.999999

/**
 * Split a path at the given params into ordered SPANS. Open paths yield cuts+1 spans
 * (start→c1, …, cn→end); closed paths yield one span per cut, each running cut→next
 * cut around the loop. Every span is an OPEN node list with sub-bulges re-derived.
 */
export function splitPathSpans(path: Path2D, cuts: PathParam[]): Path2D[] {
  const edges = edgesOf(path)
  const sorted = [...cuts].sort((a, b) => ordinal(a) - ordinal(b))
    // dedupe coincident cuts (a shared endpoint hits two edges)
    .filter((c, i, arr) => i === 0 || Math.hypot(c.pt[0] - arr[i - 1].pt[0], c.pt[1] - arr[i - 1].pt[1]) > 1e-6)
  if (!sorted.length) return [path]

  // span walk: emit the (edge, tStart→tEnd) portions from one param forward to the
  // next (wrapping on closed paths), each portion carrying its re-derived sub-bulge
  const pieces = (from: PathParam, to: PathParam): PathNode[] => {
    const nodes: PathNode[] = []
    let e = from.edge, t = from.t
    const total = edges.length
    for (let guard = 0; guard < total * 2 + 2; guard++) {
      const eg = edges[e]
      const endsHere = e === to.edge && (guard > 0 || to.t >= t - EPS)
      const tEnd = endsHere ? to.t : 1
      if (tEnd > t + 1e-9) {
        const bg = portionBulge(eg, t, tEnd)
        nodes.push({ id: newNodeId(), pt: edgePointAt(eg, t), ...(bg !== undefined ? { bulge: bg } : {}) })
      } else if (!endsHere) {
        // zero-length remainder on this edge — move on without emitting
      }
      if (endsHere) {
        nodes.push({ id: newNodeId(), pt: edgePointAt(eg, tEnd) })
        return nodes
      }
      e = (e + 1) % total
      t = 0
    }
    return nodes
  }

  const spans: Path2D[] = []
  if (path.closed) {
    for (let i = 0; i < sorted.length; i++) {
      const A = sorted[i]
      const B = sorted[(i + 1) % sorted.length]
      spans.push({ closed: false, nodes: pieces(A, B) })
    }
  } else {
    const start: PathParam = { edge: 0, t: 0, pt: edges[0].a }
    const end: PathParam = { edge: edges.length - 1, t: 1, pt: edges[edges.length - 1].b }
    const stops = [start, ...sorted, end]
    for (let i = 0; i < stops.length - 1; i++) {
      const nodes = pieces(stops[i], stops[i + 1])
      spans.push({ closed: false, nodes })
    }
  }
  // drop degenerate spans (zero length)
  return spans.filter((s) => {
    if (s.nodes.length < 2) return false
    let len = 0
    for (let i = 0; i < s.nodes.length - 1; i++) {
      len += Math.hypot(s.nodes[i + 1].pt[0] - s.nodes[i].pt[0], s.nodes[i + 1].pt[1] - s.nodes[i].pt[1])
    }
    return len > 1e-6
  })
}

// ---------- TRIM / EXTEND / FILLET / OFFSET ----------

/** the other line/region records on the same surface — the default cutter/boundary set */
function peersOf(model: ModelDoc, rec: DraftingRecord): DraftingRecord[] {
  return listDrafting(model).filter((r) =>
    r.id !== rec.id
    && (r.kind === 'line' || r.kind === 'region')
    && !!r.path
    && JSON.stringify(r.scope) === JSON.stringify(rec.scope))
}

/**
 * TRIM: remove the clicked portion of a LINE record between its intersections with
 * the cutting edges (default: every other line/region on the surface — the modern
 * AutoCAD Enter-selects-all default). The clicked span between two bracketing cuts
 * disappears; remainders stay (a middle cut splits the record in two, keeping
 * layer/pen). Closed paths open at the removed span. False = nothing to trim.
 */
export function trimDraftLine(
  model: ModelDoc,
  id: DraftingId,
  clickPt: Pt2,
  opts?: { cutterIds?: DraftingId[] },
): boolean {
  const rec = getDrafting(model, id)
  if (!rec || rec.kind !== 'line' || !rec.path || firmDetailLocked(model, rec.scope)) return false
  const cutters = opts?.cutterIds
    ? opts.cutterIds.map((cid) => getDrafting(model, cid)).filter((r): r is DraftingRecord => !!r?.path)
    : peersOf(model, rec)
  const cuts: PathParam[] = []
  for (const c of cutters) cuts.push(...pathIntersectionParams(rec.path, c.path!))
  if (!cuts.length) return false
  const spans = splitPathSpans(rec.path, cuts)
  if (spans.length < 2) return false
  // the clicked span: nearest span midpoint-projection to the click
  let clicked = 0
  let bd = Infinity
  spans.forEach((s, i) => {
    const p = projectToPathParam(s, clickPt)
    const d = Math.hypot(clickPt[0] - p.pt[0], clickPt[1] - p.pt[1])
    if (d < bd) { bd = d; clicked = i }
  })
  const keep = spans.filter((_s, i) => i !== clicked)
  if (!keep.length) return false
  transact(model, () => {
    const { drafting } = maps(model)
    const m = drafting.get(rec.id)
    if (!m) return
    m.set('path', keep[0])
    for (const extra of keep.slice(1)) {
      const copy = copyRecord(rec, { path: extra })
      drafting.set(copy.id, recordToYMap(copy as unknown as Record<string, unknown>))
    }
  })
  return true
}

/**
 * SPLIT (2026-10-01, Matt: "I should be able to use Split as a modify command in
 * drafting view on lines"): cut a LINE record at the point on it nearest `at`. An open
 * line becomes two records (the original keeps the first half and its id, the copy
 * keeps layer/pen/group); a closed line OPENS at that point. Nodes that survive keep
 * their ids, so a dimension on the kept half stays attached. False = nothing to cut
 * (an open line's own end, or not a line).
 */
export function splitDraftLine(model: ModelDoc, id: DraftingId, at: Pt2): boolean {
  const rec = getDrafting(model, id)
  if (!rec || rec.kind !== 'line' || !rec.path || rec.path.nodes.length < 2 || firmDetailLocked(model, rec.scope)) return false
  const cut = projectToPathParam(rec.path, at)
  let spans: Path2D[]
  if (rec.path.closed) {
    // one cut can't divide a loop (splitPathSpans needs two) — unroll it into an open
    // path whose last edge is the closing edge (same edge order, so the cut's edge
    // index still applies), split that, and rejoin the halves AT the cut
    const nodes = rec.path.nodes
    const unrolled: Path2D = { closed: false, nodes: [...nodes, { ...nodes[0], id: newNodeId(), bulge: undefined }] }
    const halves = splitPathSpans(unrolled, [cut])
    if (halves.length < 2) {
      // the cut sits on the loop's first node — just open it there
      const one = halves[0] ?? unrolled
      spans = [{ closed: false, nodes: one.nodes }]
    } else {
      const [head, tail] = halves
      spans = [{ closed: false, nodes: [...tail.nodes.slice(0, -1), ...head.nodes] }]
    }
  } else {
    spans = splitPathSpans(rec.path, [cut])
    if (spans.length < 2) return false
  }
  // keep the original node ids wherever a node survives unchanged
  const orig = rec.path.nodes
  const used = new Set<string>()
  const keepIds = (sp: Path2D): Path2D => ({
    ...sp,
    nodes: sp.nodes.map((nd) => {
      const o = orig.find((q) => !used.has(q.id) && Math.hypot(q.pt[0] - nd.pt[0], q.pt[1] - nd.pt[1]) < 1e-9)
      if (!o) return nd
      used.add(o.id)
      return { ...nd, id: o.id }
    }),
  })
  transact(model, () => {
    const { drafting } = maps(model)
    const m = drafting.get(rec.id)
    if (!m) return
    m.set('path', keepIds(spans[0]))
    for (const extra of spans.slice(1)) {
      const copy = copyRecord(rec, { path: extra })
      drafting.set(copy.id, recordToYMap(copy as unknown as Record<string, unknown>))
    }
  })
  return true
}

/**
 * EXTEND: lengthen the END of an open LINE record (the end nearer the click) to its
 * nearest intersection with the boundary edges (default: all other line/region
 * records). Straight ends extend along their own line; arc ends along their own
 * circle (bulge re-derived from the grown sweep). False = no boundary ahead.
 */
export function extendDraftLine(
  model: ModelDoc,
  id: DraftingId,
  clickPt: Pt2,
  opts?: { boundaryIds?: DraftingId[]; boundaryPaths?: Path2D[] },
): boolean {
  const rec = getDrafting(model, id)
  if (!rec || rec.kind !== 'line' || !rec.path || rec.path.closed || firmDetailLocked(model, rec.scope)) return false
  const path = rec.path
  const n = path.nodes.length
  if (n < 2) return false
  const d0 = Math.hypot(clickPt[0] - path.nodes[0].pt[0], clickPt[1] - path.nodes[0].pt[1])
  const dN = Math.hypot(clickPt[0] - path.nodes[n - 1].pt[0], clickPt[1] - path.nodes[n - 1].pt[1])
  const atStart = d0 < dN
  const endIdx = atStart ? 0 : n - 1
  const edgeFrom = atStart ? 0 : n - 2
  const eg = edgesOf(path)[edgeFrom]
  const endPt = path.nodes[endIdx].pt
  const boundaries: { path?: Path2D }[] = opts?.boundaryPaths
    ? opts.boundaryPaths.map((bp) => ({ path: bp }))
    : opts?.boundaryIds
      ? opts.boundaryIds.map((bid) => getDrafting(model, bid)).filter((r): r is DraftingRecord => !!r?.path)
      : peersOf(model, rec)

  // candidate hits on the end edge's infinite carrier, BEYOND the end point
  let best: { pt: Pt2; d: number } | null = null
  const consider = (pt: Pt2, beyond: boolean, d: number) => {
    if (!beyond || d < 1e-6) return
    if (!best || d < best.d) best = { pt, d }
  }
  for (const b of boundaries) {
    for (const be of edgesOf(b.path!)) {
      let roots: Pt2[] = []
      if (eg.arc) {
        roots = be.arc
          ? circleCircleIntersect(eg.arc.c, eg.arc.r, be.arc.c, be.arc.r)
          : lineCircleIntersect(be.a, [be.b[0] - be.a[0], be.b[1] - be.a[1]], eg.arc.c, eg.arc.r)
      } else {
        const dir: Pt2 = atStart
          ? [eg.a[0] - eg.b[0], eg.a[1] - eg.b[1]]
          : [eg.b[0] - eg.a[0], eg.b[1] - eg.a[1]]
        // intersect the end ray with the boundary edge's own carrier, bounded to it
        const cand = be.arc
          ? lineCircleIntersect(endPt, dir, be.arc.c, be.arc.r)
          : (() => {
            const d2: Pt2 = [be.b[0] - be.a[0], be.b[1] - be.a[1]]
            const cross = dir[0] * d2[1] - dir[1] * d2[0]
            if (Math.abs(cross) < EPS) return [] as Pt2[]
            const dx = be.a[0] - endPt[0], dy = be.a[1] - endPt[1]
            const t = (dx * d2[1] - dy * d2[0]) / cross
            return [[endPt[0] + dir[0] * t, endPt[1] + dir[1] * t]] as Pt2[]
          })()
        for (const pt of cand) {
          if (onEdge(be, pt) === null) continue
          const along = (pt[0] - endPt[0]) * dir[0] + (pt[1] - endPt[1]) * dir[1]
          consider(pt, along > EPS, Math.hypot(pt[0] - endPt[0], pt[1] - endPt[1]))
        }
        continue
      }
      // arc end edge: roots on the circle — beyond = additional sweep in travel direction
      for (const pt of roots) {
        if (onEdge(be, pt) === null) continue
        const arc = eg.arc!
        const ang = Math.atan2(pt[1] - arc.c[1], pt[0] - arc.c[0])
        const endAng = atStart ? arc.a0 : arc.a0 + arc.sweep
        const travel = atStart ? -Math.sign(arc.sweep) : Math.sign(arc.sweep)
        const norm = (v: number) => { let x = v % (Math.PI * 2); if (x < 0) x += Math.PI * 2; return x }
        const extra = travel >= 0 ? norm(ang - endAng) : norm(endAng - ang)
        if (extra < 1e-6 || extra > Math.PI * 2 - Math.abs(arc.sweep) - 1e-6) continue
        consider(pt, true, extra * arc.r)
      }
    }
  }
  const hit = best as { pt: Pt2; d: number } | null
  if (!hit) return false
  const out = { closed: false, nodes: path.nodes.map((nd) => ({ ...nd })) }
  out.nodes[endIdx] = { ...out.nodes[endIdx], pt: hit.pt }
  if (eg.arc) {
    // the end edge's sweep grew — re-derive its bulge about the SAME circle
    const arc = eg.arc
    const from = atStart ? hit.pt : path.nodes[n - 2].pt
    const to = atStart ? path.nodes[1].pt : hit.pt
    const angF = Math.atan2(from[1] - arc.c[1], from[0] - arc.c[0])
    const angT = Math.atan2(to[1] - arc.c[1], to[0] - arc.c[0])
    const norm = (v: number) => { let x = v % (Math.PI * 2); if (x < 0) x += Math.PI * 2; return x }
    const sweep = arc.sweep >= 0 ? norm(angT - angF) : -norm(angF - angT)
    out.nodes[atStart ? 0 : n - 2].bulge = Math.tan(sweep / 4)
  }
  transact(model, () => { maps(model).drafting.get(rec.id)?.set('path', out) })
  return true
}

/**
 * TRIM/EXTEND TO A REFERENCE (2026-10-01, Matt: "select the reference line to trim or
 * extend to first, then the subsequent lines selected are what get trimmed or
 * extended to that line" — Revit's Trim/Extend to boundary, in every view). The
 * reference is a LINE (two points), treated as infinite — a line that falls short of
 * the reference's drawn ends still extends onto its line, as in Revit.
 *  - the target CROSSES the reference → it is cut there and the portion you clicked
 *    is kept (everything past the reference goes);
 *  - it doesn't reach → the end that faces the reference extends onto it (the end
 *    nearer the click first, else the other end).
 * Open LINE records only. False = nothing to do (parallel, or already on it).
 */
export function trimExtendDraftLineTo(
  model: ModelDoc,
  id: DraftingId,
  clickPt: Pt2,
  ref: { a: Pt2; b: Pt2 },
): boolean {
  const rec = getDrafting(model, id)
  if (!rec || rec.kind !== 'line' || !rec.path || rec.path.closed || firmDetailLocked(model, rec.scope)) return false
  const dx = ref.b[0] - ref.a[0], dy = ref.b[1] - ref.a[1]
  const L = Math.hypot(dx, dy)
  if (L < 1e-9) return false
  const ux = dx / L, uy = dy / L
  const far = 1e5
  const refPath: Path2D = {
    closed: false,
    nodes: [
      { id: 'tx-a' as never, pt: [ref.a[0] - ux * far, ref.a[1] - uy * far] },
      { id: 'tx-b' as never, pt: [ref.a[0] + ux * far, ref.a[1] + uy * far] },
    ],
  }
  const path = rec.path
  const n = path.nodes.length
  // a crossing at the very END of the line isn't a trim — that end is already on it
  const endOn = (pt: Pt2) => [path.nodes[0].pt, path.nodes[n - 1].pt]
    .some((q) => Math.hypot(q[0] - pt[0], q[1] - pt[1]) < 1e-6)
  const cuts = pathIntersectionParams(path, refPath).filter((c) => !endOn(c.pt))
  if (cuts.length) {
    const spans = splitPathSpans(path, cuts)
    if (spans.length < 2) return false
    let keep = 0
    let bd = Infinity
    spans.forEach((sp, i) => {
      const q = projectToPathParam(sp, clickPt)
      const d = Math.hypot(clickPt[0] - q.pt[0], clickPt[1] - q.pt[1])
      if (d < bd) { bd = d; keep = i }
    })
    transact(model, () => { maps(model).drafting.get(rec.id)?.set('path', spans[keep]) })
    return true
  }
  if (extendDraftLine(model, id, clickPt, { boundaryPaths: [refPath] })) return true
  // the nearer end points away from the reference — try the other end
  const d0 = Math.hypot(clickPt[0] - path.nodes[0].pt[0], clickPt[1] - path.nodes[0].pt[1])
  const dN = Math.hypot(clickPt[0] - path.nodes[n - 1].pt[0], clickPt[1] - path.nodes[n - 1].pt[1])
  const other = d0 < dN ? path.nodes[n - 1].pt : path.nodes[0].pt
  return extendDraftLine(model, id, other, { boundaryPaths: [refPath] })
}

/**
 * FILLET (v1 — straight segments): radius-join two picked STRAIGHT segments.
 * Same record + adjacent edges → the shared corner gets the arc (two tangent nodes +
 * a bulge edge). Two OPEN records picked on their END edges → both ends trim/extend
 * to the tangent points and the records JOIN into one (B's nodes fold into A),
 * with the arc edge between — radius 0 joins sharp at the intersection. Everything
 * else refuses (arc fillets and mid-segment cases are recorded follow-ons).
 */
export function filletDraftLines(
  model: ModelDoc,
  a: { id: DraftingId; pt: Pt2 },
  b: { id: DraftingId; pt: Pt2 },
  radiusFt: number,
): boolean {
  const ra = getDrafting(model, a.id)
  const rb = getDrafting(model, b.id)
  if (!ra?.path || !rb?.path || ra.kind !== 'line' || rb.kind !== 'line') return false
  if (firmDetailLocked(model, ra.scope) || firmDetailLocked(model, rb.scope)) return false
  const pa = projectToPathParam(ra.path, a.pt)
  const pb = projectToPathParam(rb.path, b.pt)
  const ea = edgesOf(ra.path)[pa.edge]
  const eb = edgesOf(rb.path)[pb.edge]
  if (!ea || !eb || ea.arc || eb.arc) return false

  // the corner: intersection of the two segment carriers
  const d1: Pt2 = [ea.b[0] - ea.a[0], ea.b[1] - ea.a[1]]
  const d2: Pt2 = [eb.b[0] - eb.a[0], eb.b[1] - eb.a[1]]
  const cross = d1[0] * d2[1] - d1[1] * d2[0]
  if (Math.abs(cross) < EPS) return false          // parallel — no corner
  const dx = eb.a[0] - ea.a[0], dy = eb.a[1] - ea.a[1]
  const tX = (dx * d2[1] - dy * d2[0]) / cross
  const X: Pt2 = [ea.a[0] + d1[0] * tX, ea.a[1] + d1[1] * tX]

  const unit = (v: Pt2): Pt2 => {
    const l = Math.hypot(v[0], v[1]) || 1
    return [v[0] / l, v[1] / l]
  }

  if (ra.id === rb.id) {
    // SAME record: the picked edges must share a corner node (adjacent edges)
    const path = ra.path
    const lo = Math.min(pa.edge, pb.edge), hi = Math.max(pa.edge, pb.edge)
    const adjacent = hi === lo + 1 || (path.closed && lo === 0 && hi === edgesOf(path).length - 1)
    if (!adjacent) return false
    const cornerIdx = (path.closed && lo === 0 && hi === edgesOf(path).length - 1) ? 0 : hi
    const nPrev = path.nodes[(cornerIdx - 1 + path.nodes.length) % path.nodes.length]
    const nCorner = path.nodes[cornerIdx]
    const nNext = path.nodes[(cornerIdx + 1) % path.nodes.length]
    const uA = unit([nPrev.pt[0] - nCorner.pt[0], nPrev.pt[1] - nCorner.pt[1]])
    const uB = unit([nNext.pt[0] - nCorner.pt[0], nNext.pt[1] - nCorner.pt[1]])
    const cosPhi = uA[0] * uB[0] + uA[1] * uB[1]
    const phi = Math.acos(Math.max(-1, Math.min(1, cosPhi)))
    if (phi < 1e-6 || Math.PI - phi < 1e-6) return false
    const out = { closed: path.closed, nodes: path.nodes.map((nd) => ({ ...nd })) }
    if (radiusFt <= 0) {
      // radius 0 (Matt: "a corner join that trims/extends two lines to each
      // other at once"): the adjacent edges of a single polyline already share
      // this node, so there's nothing to trim/extend — just collapse the
      // corner to one plain sharp node at X, dropping any bulge (un-fillets a
      // previously-rounded corner picked back through its own straight edges).
      out.nodes.splice(cornerIdx, 1, { id: newNodeId(), pt: X })
      transact(model, () => { maps(model).drafting.get(ra.id)?.set('path', out) })
      return true
    }
    const d = radiusFt / Math.tan(phi / 2)
    const Ta: Pt2 = [nCorner.pt[0] + uA[0] * d, nCorner.pt[1] + uA[1] * d]
    const Tb: Pt2 = [nCorner.pt[0] + uB[0] * d, nCorner.pt[1] + uB[1] * d]
    // arc sweep = π − φ; direction = the turn from (−uA) into uB
    const sweep = (Math.PI - phi) * Math.sign((-uA[0]) * uB[1] - (-uA[1]) * uB[0])
    out.nodes.splice(cornerIdx, 1,
      { id: newNodeId(), pt: Ta, bulge: Math.tan(sweep / 4) },
      { id: newNodeId(), pt: Tb },
    )
    transact(model, () => { maps(model).drafting.get(ra.id)?.set('path', out) })
    return true
  }

  // TWO OPEN records: the picked edges must be END edges; the free end trims to the corner
  if (ra.path.closed || rb.path.closed) return false
  const endInfo = (rec: DraftingRecord, edge: number): { endIdx: number; inner: Pt2 } | null => {
    const path = rec.path!
    const count = path.nodes.length
    if (edge === 0 && count >= 2) {
      // could be the start end — unless the path is a single edge where both apply;
      // choose the endpoint FARTHER from the corner X to stay, nearer one to trim
      const dStart = Math.hypot(path.nodes[0].pt[0] - X[0], path.nodes[0].pt[1] - X[1])
      const dEnd = Math.hypot(path.nodes[count - 1].pt[0] - X[0], path.nodes[count - 1].pt[1] - X[1])
      if (count === 2) return dStart <= dEnd ? { endIdx: 0, inner: path.nodes[1].pt } : { endIdx: 1, inner: path.nodes[0].pt }
      return { endIdx: 0, inner: path.nodes[1].pt }
    }
    if (edge === count - 2) return { endIdx: count - 1, inner: path.nodes[count - 2].pt }
    return null
  }
  const infoA = endInfo(ra, pa.edge)
  const infoB = endInfo(rb, pb.edge)
  if (!infoA || !infoB) return false
  const uA = unit([infoA.inner[0] - X[0], infoA.inner[1] - X[1]])
  const uB = unit([infoB.inner[0] - X[0], infoB.inner[1] - X[1]])
  const cosPhi = uA[0] * uB[0] + uA[1] * uB[1]
  const phi = Math.acos(Math.max(-1, Math.min(1, cosPhi)))
  if (phi < 1e-6 || Math.PI - phi < 1e-6) return false
  const d = radiusFt > 0 ? radiusFt / Math.tan(phi / 2) : 0
  const Ta: Pt2 = [X[0] + uA[0] * d, X[1] + uA[1] * d]
  const Tb: Pt2 = [X[0] + uB[0] * d, X[1] + uB[1] * d]

  // A's nodes ordered so its trimmed end comes LAST; B's so its trimmed end comes FIRST
  const orient = (rec: DraftingRecord, endIdx: number, endLast: boolean): PathNode[] => {
    const nodes = rec.path!.nodes.map((nd) => ({ ...nd }))
    const endsAtTail = endIdx === nodes.length - 1
    if (endLast !== endsAtTail) {
      // reverse an open path: bulges re-key to the new from-node and negate
      const rev: PathNode[] = []
      for (let i = nodes.length - 1; i >= 0; i--) {
        const nd = nodes[i]
        const prev = i > 0 ? nodes[i - 1] : null
        rev.push({
          id: nd.id, pt: nd.pt,
          ...(prev?.bulge !== undefined ? { bulge: -prev.bulge } : {}),
        })
      }
      return rev
    }
    return nodes
  }
  const aNodes = orient(ra, infoA.endIdx, true)
  const bNodes = orient(rb, infoB.endIdx, false)
  aNodes[aNodes.length - 1] = { ...aNodes[aNodes.length - 1], pt: Ta }
  bNodes[0] = { ...bNodes[0], pt: Tb }
  const joined: PathNode[] = [...aNodes]
  if (radiusFt > 0) {
    const sweep = (Math.PI - phi) * Math.sign((-uA[0]) * uB[1] - (-uA[1]) * uB[0])
    joined[joined.length - 1] = { ...joined[joined.length - 1], bulge: Math.tan(sweep / 4) }
    joined.push(...bNodes)
  } else {
    // sharp corner (radius 0): both trimmed ends land ON X — one shared corner node
    const rest = bNodes.slice(1)
    if (!rest.length) return false
    joined[joined.length - 1] = { ...joined[joined.length - 1], pt: X }
    joined.push(...rest)
  }
  transact(model, () => {
    const { drafting } = maps(model)
    const m = drafting.get(ra.id)
    if (!m) return
    m.set('path', { closed: false, nodes: joined })
    drafting.delete(rb.id)
  })
  return true
}

/**
 * CHAMFER (Matt: "there is fillet and chamfer — chamfer is the angle version"):
 * FILLET's straight-line-cut sibling — instead of a rounded arc, the corner is
 * replaced by a straight segment landing `dist1Ft` from the corner along the
 * FIRST picked edge and `dist2Ft` along the second. Same two shapes as fillet:
 * a same-record adjacent corner (polyline) or two open-record ends (join) —
 * everything else refuses, identically to fillet. Both distances 0 degenerates
 * to fillet's own radius-0 sharp corner (no special-casing needed — the plain
 * distance math already lands both trim points on the corner itself).
 */
export function chamferDraftLines(
  model: ModelDoc,
  a: { id: DraftingId; pt: Pt2 },
  b: { id: DraftingId; pt: Pt2 },
  dist1Ft: number,
  dist2Ft: number,
): boolean {
  if (dist1Ft < 0 || dist2Ft < 0) return false
  const ra = getDrafting(model, a.id)
  const rb = getDrafting(model, b.id)
  if (!ra?.path || !rb?.path || ra.kind !== 'line' || rb.kind !== 'line') return false
  if (firmDetailLocked(model, ra.scope) || firmDetailLocked(model, rb.scope)) return false
  const pa = projectToPathParam(ra.path, a.pt)
  const pb = projectToPathParam(rb.path, b.pt)
  const ea = edgesOf(ra.path)[pa.edge]
  const eb = edgesOf(rb.path)[pb.edge]
  if (!ea || !eb || ea.arc || eb.arc) return false

  // the corner: intersection of the two segment carriers
  const d1: Pt2 = [ea.b[0] - ea.a[0], ea.b[1] - ea.a[1]]
  const d2: Pt2 = [eb.b[0] - eb.a[0], eb.b[1] - eb.a[1]]
  const cross = d1[0] * d2[1] - d1[1] * d2[0]
  if (Math.abs(cross) < EPS) return false          // parallel — no corner
  const dx = eb.a[0] - ea.a[0], dy = eb.a[1] - ea.a[1]
  const tX = (dx * d2[1] - dy * d2[0]) / cross
  const X: Pt2 = [ea.a[0] + d1[0] * tX, ea.a[1] + d1[1] * tX]

  const unit = (v: Pt2): Pt2 => {
    const l = Math.hypot(v[0], v[1]) || 1
    return [v[0] / l, v[1] / l]
  }

  if (ra.id === rb.id) {
    // SAME record: the picked edges must share a corner node (adjacent edges)
    const path = ra.path
    const lo = Math.min(pa.edge, pb.edge), hi = Math.max(pa.edge, pb.edge)
    const adjacent = hi === lo + 1 || (path.closed && lo === 0 && hi === edgesOf(path).length - 1)
    if (!adjacent) return false
    const cornerIdx = (path.closed && lo === 0 && hi === edgesOf(path).length - 1) ? 0 : hi
    const nPrev = path.nodes[(cornerIdx - 1 + path.nodes.length) % path.nodes.length]
    const nCorner = path.nodes[cornerIdx]
    const nNext = path.nodes[(cornerIdx + 1) % path.nodes.length]
    const uPrev = unit([nPrev.pt[0] - nCorner.pt[0], nPrev.pt[1] - nCorner.pt[1]])
    const uNext = unit([nNext.pt[0] - nCorner.pt[0], nNext.pt[1] - nCorner.pt[1]])
    // dist1Ft is the FIRST pick's own distance — figure out which side (prev/next) it landed on
    const aIsPrevSide = pa.edge === lo
    const distPrev = aIsPrevSide ? dist1Ft : dist2Ft
    const distNext = aIsPrevSide ? dist2Ft : dist1Ft
    const Ta: Pt2 = [nCorner.pt[0] + uPrev[0] * distPrev, nCorner.pt[1] + uPrev[1] * distPrev]
    const Tb: Pt2 = [nCorner.pt[0] + uNext[0] * distNext, nCorner.pt[1] + uNext[1] * distNext]
    const out = { closed: path.closed, nodes: path.nodes.map((nd) => ({ ...nd })) }
    if (distPrev < EPS && distNext < EPS) {
      // both distances 0 — the same degenerate sharp corner fillet radius 0 gives
      out.nodes.splice(cornerIdx, 1, { id: newNodeId(), pt: X })
    } else {
      out.nodes.splice(cornerIdx, 1, { id: newNodeId(), pt: Ta }, { id: newNodeId(), pt: Tb })
    }
    transact(model, () => { maps(model).drafting.get(ra.id)?.set('path', out) })
    return true
  }

  // TWO OPEN records: the picked edges must be END edges; the free end trims to the corner
  if (ra.path.closed || rb.path.closed) return false
  const endInfo = (rec: DraftingRecord, edge: number): { endIdx: number; inner: Pt2 } | null => {
    const path = rec.path!
    const count = path.nodes.length
    if (edge === 0 && count >= 2) {
      const dStart = Math.hypot(path.nodes[0].pt[0] - X[0], path.nodes[0].pt[1] - X[1])
      const dEnd = Math.hypot(path.nodes[count - 1].pt[0] - X[0], path.nodes[count - 1].pt[1] - X[1])
      if (count === 2) return dStart <= dEnd ? { endIdx: 0, inner: path.nodes[1].pt } : { endIdx: 1, inner: path.nodes[0].pt }
      return { endIdx: 0, inner: path.nodes[1].pt }
    }
    if (edge === count - 2) return { endIdx: count - 1, inner: path.nodes[count - 2].pt }
    return null
  }
  const infoA = endInfo(ra, pa.edge)
  const infoB = endInfo(rb, pb.edge)
  if (!infoA || !infoB) return false
  const uA = unit([infoA.inner[0] - X[0], infoA.inner[1] - X[1]])
  const uB = unit([infoB.inner[0] - X[0], infoB.inner[1] - X[1]])
  const Ta: Pt2 = [X[0] + uA[0] * dist1Ft, X[1] + uA[1] * dist1Ft]
  const Tb: Pt2 = [X[0] + uB[0] * dist2Ft, X[1] + uB[1] * dist2Ft]

  const orient = (rec: DraftingRecord, endIdx: number, endLast: boolean): PathNode[] => {
    const nodes = rec.path!.nodes.map((nd) => ({ ...nd }))
    const endsAtTail = endIdx === nodes.length - 1
    if (endLast !== endsAtTail) {
      const rev: PathNode[] = []
      for (let i = nodes.length - 1; i >= 0; i--) {
        const nd = nodes[i]
        const prev = i > 0 ? nodes[i - 1] : null
        rev.push({
          id: nd.id, pt: nd.pt,
          ...(prev?.bulge !== undefined ? { bulge: -prev.bulge } : {}),
        })
      }
      return rev
    }
    return nodes
  }
  const aNodes = orient(ra, infoA.endIdx, true)
  const bNodes = orient(rb, infoB.endIdx, false)
  aNodes[aNodes.length - 1] = { ...aNodes[aNodes.length - 1], pt: Ta }
  bNodes[0] = { ...bNodes[0], pt: Tb }
  const joined: PathNode[] = [...aNodes]
  if (dist1Ft < EPS && dist2Ft < EPS) {
    // sharp corner (both distances 0): both trimmed ends land ON X — one shared node
    const rest = bNodes.slice(1)
    if (!rest.length) return false
    joined[joined.length - 1] = { ...joined[joined.length - 1], pt: X }
    joined.push(...rest)
  } else {
    joined.push(...bNodes)
  }
  transact(model, () => {
    const { drafting } = maps(model)
    const m = drafting.get(ra.id)
    if (!m) return
    m.set('path', { closed: false, nodes: joined })
    drafting.delete(rb.id)
  })
  return true
}

/**
 * OFFSET: a parallel COPY of a line/region record at `distFt`, on the side of
 * `sidePt` (the AutoCAD offset-through-side click) — the mitered, arc-aware
 * offsetPolyline under the hood. The copy keeps layer/pen/pattern; fresh ids.
 */
/**
 * The shared LEFT/RIGHT decision + moved path — extracted (CAD r5) so a live ghost
 * preview and the eventual commit can never disagree: which side is the click on?
 * The nearest edge's left normal decides the sign.
 */
function offsetMovedPath(rec: DraftingRecord, distFt: number, sidePt: Pt2): Path2D | null {
  if (!rec.path) return null
  const p = projectToPathParam(rec.path, sidePt)
  const eg = edgesOf(rec.path)[p.edge]
  let leftSide: boolean
  if (eg.arc) {
    // left of travel on a CCW (+sweep) arc = toward the center; CW = away
    const dC = Math.hypot(sidePt[0] - eg.arc.c[0], sidePt[1] - eg.arc.c[1])
    leftSide = eg.arc.sweep >= 0 ? dC < eg.arc.r : dC > eg.arc.r
  } else {
    const dxE = eg.b[0] - eg.a[0], dyE = eg.b[1] - eg.a[1]
    leftSide = dxE * (sidePt[1] - eg.a[1]) - dyE * (sidePt[0] - eg.a[0]) > 0
  }
  return offsetPolyline(rec.path, leftSide ? distFt : -distFt)
}

export function offsetDraftRecord(
  model: ModelDoc,
  id: DraftingId,
  distFt: number,
  sidePt: Pt2,
): DraftingId | null {
  const rec = getDrafting(model, id)
  if (!rec?.path || (rec.kind !== 'line' && rec.kind !== 'region')) return null
  if (firmDetailLocked(model, rec.scope) || !(distFt > 0)) return null
  const moved = offsetMovedPath(rec, distFt, sidePt)
  if (!moved) return null
  const copy = copyRecord(rec, { path: moved })
  transact(model, () => {
    maps(model).drafting.set(copy.id, recordToYMap(copy as unknown as Record<string, unknown>))
  })
  return copy.id
}

/**
 * CAD r5: a non-mutating preview of what OFFSET would draw, for the command's live
 * ghost while the user is still choosing the side/through point — flattened points,
 * ready for the annotation prim vocabulary. Refuses exactly like offsetDraftRecord
 * (firm-locked, non-positive distance) so a ghost never promises what the commit
 * would then refuse.
 */
export function previewOffsetPath(model: ModelDoc, id: DraftingId, distFt: number, sidePt: Pt2): Pt2[] | null {
  const rec = getDrafting(model, id)
  if (!rec?.path || (rec.kind !== 'line' && rec.kind !== 'region')) return null
  if (firmDetailLocked(model, rec.scope) || !(distFt > 0)) return null
  const moved = offsetMovedPath(rec, distFt, sidePt)
  return moved ? flattenPathPts(moved) : null
}

// ---------- DRAW ORDER round (Matt: "a draw ordering system like Revit has") ----------

function surfaceOfScope(scope: DraftingScope): DraftingSurface {
  return scope.kind === 'level' ? { kind: 'level', levelId: scope.levelId } : { kind: 'view', viewId: scope.viewId }
}

/**
 * The FIRST reorder command touching a scope backfills every sibling's zOrder
 * from its CURRENT natural position, in one transaction — after this, the scope
 * is "all-explicit" (draftingForSurface's own contract: a scope is either
 * all-absent or all-explicit, never a mix) and every future reorder is a plain
 * numeric operation. A no-op, and returns the untouched list, once a scope is
 * already all-explicit. Returns the (possibly freshly-stamped) sibling list.
 */
function ensureZOrderBackfilled(model: ModelDoc, surface: DraftingSurface): DraftingRecord[] {
  const recs = draftingForSurface(model, surface)
  // (2026-10-13) also NORMALIZES a scope where records drawn after an earlier reorder
  // carry no zOrder yet — every record takes its current position, so a step is real
  if (recs.some((r) => r.zOrder == null)) {
    const { drafting } = maps(model)
    transact(model, () => {
      recs.forEach((r, i) => drafting.get(r.id)?.set('zOrder', i))
    })
    return draftingForSurface(model, surface)
  }
  return recs
}

/** Bring to Front: lands strictly above every sibling on the same surface. */
export function bringDraftingToFront(model: ModelDoc, id: DraftingId): boolean {
  const rec = getDrafting(model, id)
  if (!rec || firmDetailLocked(model, rec.scope)) return false
  const siblings = ensureZOrderBackfilled(model, surfaceOfScope(rec.scope))
  const maxZ = siblings.reduce((m, r) => Math.max(m, r.zOrder ?? 0), 0)
  const em = maps(model).drafting.get(id)
  if (!em) return false
  transact(model, () => { em.set('zOrder', maxZ + 1) })
  return true
}

/** Send to Back: lands strictly below every sibling on the same surface. */
export function sendDraftingToBack(model: ModelDoc, id: DraftingId): boolean {
  const rec = getDrafting(model, id)
  if (!rec || firmDetailLocked(model, rec.scope)) return false
  const siblings = ensureZOrderBackfilled(model, surfaceOfScope(rec.scope))
  const minZ = siblings.reduce((m, r) => Math.min(m, r.zOrder ?? 0), 0)
  const em = maps(model).drafting.get(id)
  if (!em) return false
  transact(model, () => { em.set('zOrder', minZ - 1) })
  return true
}

/**
 * Bring Forward / Send Backward: swaps with the immediate neighbor ONE step
 * toward the front/back in the CURRENT stack — a true single-step reorder
 * regardless of how sparse the stored values are (never a naive ±1 on the raw
 * number). A tie (both sides share one value — the just-backfilled case, every
 * neighbor initially 1 apart so this is rare in practice) nudges instead of a
 * no-op swap, so the command always visibly does something the first time it's
 * used. Returns false at either end of the stack (nothing to move past).
 */
function stepDraftingOrder(model: ModelDoc, id: DraftingId, dir: 1 | -1): boolean {
  const rec = getDrafting(model, id)
  if (!rec || firmDetailLocked(model, rec.scope)) return false
  const ordered = ensureZOrderBackfilled(model, surfaceOfScope(rec.scope))
  const i = ordered.findIndex((r) => r.id === id)
  const j = i + dir
  if (i < 0 || j < 0 || j >= ordered.length) return false
  const a = ordered[i], b = ordered[j]
  const za = a.zOrder ?? 0, zb = b.zOrder ?? 0
  const ema = maps(model).drafting.get(a.id)
  const emb = maps(model).drafting.get(b.id)
  if (!ema || !emb) return false
  transact(model, () => {
    if (za === zb) ema.set('zOrder', zb + dir)
    else { ema.set('zOrder', zb); emb.set('zOrder', za) }
  })
  return true
}

export function bringDraftingForward(model: ModelDoc, id: DraftingId): boolean {
  return stepDraftingOrder(model, id, 1)
}
export function sendDraftingBackward(model: ModelDoc, id: DraftingId): boolean {
  return stepDraftingOrder(model, id, -1)
}

/**
 * DRAW ORDER BY UNIT (2026-10-13, Matt: "I need to be able to move items forward or
 * backward in visual stacking order like it's done in Revit"). Revit's Arrange tools
 * (Bring to Front · Bring Forward · Send Backward · Send to Back) move the SELECTED
 * DETAILS relative to the OTHER details in the view — a detail component or a detail
 * group is one detail. Here a group (every placed component is one) is one UNIT; the
 * per-record ops above stepped a component past its own pieces, which looked like
 * nothing happening. `ids` = the selection (groups expand). One transaction. Returns
 * true when the stack changed.
 */
export function reorderDraftingUnits(
  model: ModelDoc, ids: DraftingId[], op: 'front' | 'back' | 'forward' | 'backward',
): boolean {
  const first = ids.map((id) => getDrafting(model, id)).find(Boolean)
  if (!first || firmDetailLocked(model, first.scope)) return false
  const ordered = ensureZOrderBackfilled(model, surfaceOfScope(first.scope))   // bottom → top
  const sel = new Set(expandDraftGroups(model, ids))
  // units, ordered by their TOPMOST record (a group's pieces may be interleaved)
  type Unit = { key: string; recs: DraftingRecord[]; top: number; selected: boolean }
  const byKey = new Map<string, Unit>()
  ordered.forEach((r, i) => {
    const key = r.groupId ? `g:${r.groupId}` : `r:${r.id}`
    let u = byKey.get(key)
    if (!u) { u = { key, recs: [], top: i, selected: false }; byKey.set(key, u) }
    u.recs.push(r)
    u.top = i
    if (sel.has(r.id)) u.selected = true
  })
  const units = [...byKey.values()].sort((a, b) => a.top - b.top)
  if (!units.some((u) => u.selected)) return false
  const before = units.map((u) => u.key).join('|')
  let next: Unit[]
  if (op === 'front') next = [...units.filter((u) => !u.selected), ...units.filter((u) => u.selected)]
  else if (op === 'back') next = [...units.filter((u) => u.selected), ...units.filter((u) => !u.selected)]
  else {
    next = [...units]
    if (op === 'forward') {
      // top-down: each selected unit hops over the unselected unit just above it
      for (let i = next.length - 2; i >= 0; i--) {
        if (next[i].selected && !next[i + 1].selected) [next[i], next[i + 1]] = [next[i + 1], next[i]]
      }
    } else {
      for (let i = 1; i < next.length; i++) {
        if (next[i].selected && !next[i - 1].selected) [next[i], next[i - 1]] = [next[i - 1], next[i]]
      }
    }
  }
  if (next.map((u) => u.key).join('|') === before) return false
  const { drafting } = maps(model)
  transact(model, () => {
    let z = 0
    for (const u of next) for (const r of u.recs) drafting.get(r.id)?.set('zOrder', z++)
  })
  return true
}

export type { DraftingScope }
